/**
 * Pure formatting helpers. No React, no side effects — easy to unit test.
 */

// ─── Currency ──────────────────────────────────────────────────
const THB_FORMATTER = new Intl.NumberFormat("th-TH", {
  style: "currency",
  currency: "THB",
  maximumFractionDigits: 0,
});

/**
 * Format a number as Thai baht currency.
 *   formatTHB(1999)    → "฿1,999"
 *   formatTHB("1999")  → "฿1,999"
 *   formatTHB(null)    → "—"
 *   formatTHB("abc")   → "—"
 */
export const formatTHB = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  return THB_FORMATTER.format(n);
};

// ─── Card number ───────────────────────────────────────────────
/**
 * Group digits with single spaces based on issuer gaps.
 * Default gaps [4, 8, 12] produce "4242 4242 4242 4242".
 * Amex uses [4, 10] → "3782 822463 10005".
 *
 * Strips non-digits before formatting so "42 42-4242..." → "4242 4242...".
 */
export const formatCardNumber = (raw, gaps = [4, 8, 12]) => {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  let out = digits;
  gaps.forEach((gap, i) => {
    if (out.length > gap + i) {
      out = out.slice(0, gap + i) + " " + out.slice(gap + i);
    }
  });
  return out;
};

// ─── Duration (session countdown) ──────────────────────────────
/**
 * Seconds → bilingual human string.
 *   formatDuration(3725, "th") → "1 ชั่วโมง 2 นาที 5 วินาที"
 *   formatDuration(3725, "en") → "1 hour 2 minutes 5 seconds"
 *   formatDuration(null, "th") → "—"
 *   formatDuration(0, "th")    → "0 วินาที"
 */
export const formatDuration = (seconds, lang = "th") => {
  if (seconds === null || seconds === undefined) return "—";
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;

  const labels = lang === "en"
    ? { h: "hour", hs: "hours", m: "minute", ms: "minutes", s: "second", ss: "seconds" }
    : { h: "ชั่วโมง", hs: "ชั่วโมง", m: "นาที", ms: "นาที", s: "วินาที", ss: "วินาที" };

  const parts = [];
  if (h > 0) parts.push(`${h} ${h === 1 ? labels.h : labels.hs}`);
  if (m > 0) parts.push(`${m} ${m === 1 ? labels.m : labels.ms}`);
  if (sec > 0 || parts.length === 0) {
    parts.push(`${sec} ${sec === 1 ? labels.s : labels.ss}`);
  }
  return parts.join(" ");
};